import { c as _c } from "react/compiler-runtime";
import { Heading, HStack, Skeleton, VStack } from '@chakra-ui/react';
import { SessionReviewList } from '@src/components/sco/Advisors/reviews/SessionReviewList';
import { SessionReviewSummary } from '@src/components/sco/Advisors/reviews/SessionReviewSummary';
import { hasSessionReviews } from '@src/components/sco/Advisors/reviews/sessionReviewHelpers';
import { useSessionReviewPages } from '@src/components/sco/Advisors/reviews/useSessionReviewPages';
import { USER_SESSION_REVIEWS } from '@src/gql/queries/SessionReviews';
import type { Query } from '@src/graphql-types';
export type UserSessionReviewsProps = {
  /** The member the reviews are about. */
  userId: string;
  /** Reviews per page (backend caps at 50). */
  pageSize?: number;
  /** Section heading. Pass null to omit. */
  heading?: string | null;
};
const selectUser = (data: Query | undefined) => data?.userById;

/**
 * Public reviews block for a member's profile: heading + summary + list with "Show more".
 * Renders nothing when the member has no reviews or the viewer can't read them.
 */
export const UserSessionReviews = t0 => {
  const $ = _c(25);
  const {
    userId,
    pageSize: t1,
    heading: t2
  } = t0;
  const pageSize = t1 === undefined ? 5 : t1;
  const heading = t2 === undefined ? "Reviews" : t2;
  let t3;
  if ($[0] !== userId) {
    t3 = {
      userId
    };
    $[0] = userId;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = !userId;
  let t5;
  if ($[2] !== pageSize || $[3] !== t3 || $[4] !== t4) {
    t5 = {
      query: USER_SESSION_REVIEWS,
      variables: t3,
      selectUser,
      pageSize,
      skip: t4
    };
    $[2] = pageSize;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    user,
    reviews,
    hasMore,
    loadMore,
    loadingMore,
    loading,
    error
  } = useSessionReviewPages(t5);
  if (loading) {
    let t6;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <VStack align="stretch" gap={3} width="full"><Skeleton height="6" width="40" /><Skeleton borderRadius="xl" height="24" /></VStack>;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    return t6;
  }
  const summary = user?.sessionReviewSummary;
  if (error || !hasSessionReviews(summary)) {
    return null;
  }
  let t6;
  if ($[7] !== heading) {
    t6 = heading && <Heading as="h2" size="md">{heading}</Heading>;
    $[7] = heading;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = summary?.averageScore;
  const t8 = summary?.count;
  let t9;
  if ($[9] !== t7 || $[10] !== t8) {
    t9 = <SessionReviewSummary averageScore={t7} count={t8} />;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t6 || $[13] !== t9) {
    t10 = <HStack flexWrap="wrap" gap={3} justify="space-between">{t6}{t9}</HStack>;
    $[12] = t6;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== loadMore) {
    t11 = () => void loadMore();
    $[15] = loadMore;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== hasMore || $[18] !== loadingMore || $[19] !== reviews || $[20] !== t11) {
    t12 = <SessionReviewList emptyText={null} hasMore={hasMore} loadingMore={loadingMore} reviews={reviews} onShowMore={t11} />;
    $[17] = hasMore;
    $[18] = loadingMore;
    $[19] = reviews;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10 || $[23] !== t12) {
    t13 = <VStack align="stretch" data-testid="user-session-reviews" gap={4} width="full">{t10}{t12}</VStack>;
    $[22] = t10;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  return t13;
};